import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { View, Animated, StyleSheet, Image } from 'react-native';
import { Box, LinearProgress } from '@mui/material';
import { appConfigStore } from '../app/stores/AppConfig';
import { getPrefixedStorageName } from '../utils/storagePrefix';

interface ConfigLoadingOverlayProps {
    logo?: string;
}

// Helper to convert image URL to base64 data URL
const imageUrlToDataUrl = async (url: string): Promise<string | null> => {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
};

// Read cached logo directly from localStorage SYNCHRONOUSLY for instant display
// This avoids waiting for Zustand hydration
const getInitialCachedLogo = (): { url: string; dataUrl: string } | null => {
    if (typeof window === 'undefined') return null;
    try {
        const storageKey = getPrefixedStorageName('app-config-storage');
        const stored = localStorage.getItem(storageKey);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed?.state?.cachedSplashLogo?.dataUrl) {
                return parsed.state.cachedSplashLogo;
            }
        }
    } catch {
        // Silently fail
    }
    return null;
};

export const ConfigLoadingOverlay: React.FC<ConfigLoadingOverlayProps> = ({ logo }) => {
    const [fadeAnim] = useState(new Animated.Value(1));
    const {
        isConfigHydrated: isHydrated,
        isAppReady,
        storeConfig,
        cachedTheme,
        darkMode,
        cachedSplashLogo,
        setCachedSplashLogo
    } = appConfigStore();

    // Track if we've already started caching to avoid duplicate requests
    const isCachingRef = useRef(false);

    // Read cached logo SYNCHRONOUSLY from localStorage on first render
    // This runs before Zustand hydrates, providing instant logo display
    const initialCachedLogo = useMemo(() => getInitialCachedLogo(), []);
    
    // Get colors from storeConfig (fresh) > cachedTheme (persisted) > default
    // This ensures we show cached colors immediately while fresh config loads
    const primaryColor = storeConfig?.config?.primaryColor || cachedTheme?.primaryColor || '#800080';
    const themeMode = storeConfig?.config?.themeMode || cachedTheme?.themeMode || 'light';
    const isDarkTheme = darkMode || themeMode === 'dark';
    
    // Use splash-specific settings: fresh > cached > appLogo > null
    const splashLogo = storeConfig?.config?.splashLogo || cachedTheme?.splashLogo || cachedTheme?.appLogo || storeConfig?.config?.appLogo || null;
    const splashTintColor = storeConfig?.config?.splashTintColor || cachedTheme?.splashTintColor || primaryColor;
    const splashLogoTintColor = storeConfig?.config?.splashLogoTintColor || null;

    // Determine logo URL: prop > splashLogo > appLogo > null
    const effectiveLogo = logo || splashLogo;

    // Use cached data URL - prioritize Zustand (if hydrated) > initial sync read
    // This ensures instant display from sync localStorage read, then Zustand takes over
    const cachedDataUrl = cachedSplashLogo?.dataUrl || initialCachedLogo?.dataUrl || null;

    // Determine logo source: cached dataUrl > remote URL > default icon
    const logoSource = effectiveLogo
        ? { uri: cachedDataUrl || effectiveLogo }
        : isDarkTheme
            ? require('../assets/icon-white-bg.svg')
            : require('../assets/icon-black-bg.svg');

    // Determine if logo should have a tint applied
    const shouldApplyTint = effectiveLogo && splashLogoTintColor;

    // Fetch logo and cache it for instant loading on future visits
    // Always fetch fresh to detect if image data changed (even if URL is same)
    const cacheLogoImage = useCallback(async (logoUrl: string) => {
        if (isCachingRef.current) return;
        isCachingRef.current = true;

        try {
            const dataUrl = await imageUrlToDataUrl(logoUrl);
            if (dataUrl) {
                // Only update cache if data actually changed (or URL changed)
                if (cachedSplashLogo?.dataUrl !== dataUrl || cachedSplashLogo?.url !== logoUrl) {
                    setCachedSplashLogo(logoUrl, dataUrl);
                }
            }
        } catch {
            // Silently fail - logo will just load from URL
        } finally {
            isCachingRef.current = false;
        }
    }, [cachedSplashLogo, setCachedSplashLogo]);

    // Cache the splash logo when available
    useEffect(() => {
        if (effectiveLogo && typeof effectiveLogo === 'string' && effectiveLogo.startsWith('http')) {
            // Always try to cache - the function handles deduplication
            cacheLogoImage(effectiveLogo);
        }
    }, [effectiveLogo, cacheLogoImage]);

    useEffect(() => {
        // Only fade out when app is FULLY ready (config + fonts + session)
        if (isAppReady && isHydrated) {
            Animated.timing(fadeAnim, {
                toValue: 0,
                duration: 800,
                delay: 1250, // Longer delay for first load
                useNativeDriver: false, // Web doesn't support native driver
            }).start();
        }
    }, [isAppReady, isHydrated, fadeAnim]);

    // Determine if overlay should block interactions
    const shouldBlockInteractions = !isAppReady;

    return (
        <Animated.View
            style={[
                styles.overlay,
                {
                    opacity: fadeAnim,
                    backgroundColor: splashTintColor,
                },
            ]}
            pointerEvents={shouldBlockInteractions ? 'auto' : 'none'}
        >
            <View style={styles.container}>
                {/* Logo with optional tint color */}
                <Image
                    source={logoSource}
                    style={[
                        styles.logo,
                        shouldApplyTint ? { tintColor: splashLogoTintColor as string } : null,
                    ]}
                    resizeMode='contain'
                />

                {/* Progress Bar */}
                <Box sx={{ width: 280, mt: 3 }}>
                    <LinearProgress
                        sx={{
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 2,
                                backgroundColor: '#FFFFFF',
                            },
                        }}
                    />
                </Box>
            </View>
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    overlay: {
        position: 'absolute' as 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        justifyContent: 'center' as 'center',
        alignItems: 'center' as 'center',
    },
    container: {
        alignItems: 'center' as 'center',
        justifyContent: 'center' as 'center',
    },
    logo: {
        width: 160,
        height: 160,
    },
});

